'use client';

import { c as _c } from "react/compiler-runtime";
import type { Cart, CartLine, ShopifyImage } from '@repo/shopify';
import { flattenConnection } from '@repo/shopify/client';
import { useEffect, useMemo, useRef } from 'react';
import { useCart } from '@/components/cart/cart-provider';
import type { ApparelBundleOffer, ApparelBundleSettings } from '@/lib/apparel-bundle-config';
import { DEFAULT_APPAREL_BUNDLE_SETTINGS, isApparelBundleCartLine } from '@/lib/apparel-bundle-config';
import { type ApparelBundlePick, type ApparelBundlePricing, type ApparelBundleSlots, computeApparelBundlePricing, createEmptySlots, fillNextSlot } from './apparel-bundle-math';
export interface ApparelBundleState {
  config: ApparelBundleSettings;
  /** This PDP's current selection — prices the ladder columns; never counted until it's in the cart. */
  current: ApparelBundlePick | null;
  /** Eligible lines already in the cart, one slot per unit. */
  slots: ApparelBundleSlots;
  pricing: ApparelBundlePricing;
}
function toAmount(amount: string | undefined | null): number | null {
  if (!amount) return null;
  const parsed = Number(amount);
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * Eligible lines already in the cart, as bundle picks. The strip is a picture
 * of the cart and nothing else: what the shopper has already added shows up
 * in the slots — including earlier units of this same product — and adding
 * this one is what fills the next slot. Until then it isn't counted.
 *
 * Eligibility is `isApparelBundleCartLine` — the checkout Function's rule —
 * so a one-time Hydrate bag or an excluded product in the cart never shows
 * as an "In cart" pick that unlocks a rung checkout won't honor.
 */
function toCartPick(line: CartLine): ApparelBundlePick | null {
  const variant = line.merchandise;
  const product = variant?.product;
  const price = toAmount(line.cost?.amountPerQuantity?.amount ?? variant?.price?.amount);
  if (!variant || !product || price == null) return null;
  return {
    handle: product.handle,
    productId: product.id,
    title: product.title,
    productType: product.productType,
    merchandiseId: variant.id,
    variantTitle: variant.title ?? null,
    vendor: product.vendor,
    image: (variant.image ?? product.featuredImage ?? null) as ShopifyImage | null,
    price,
    compareAt: toAmount(line.cost?.compareAtAmountPerQuantity?.amount),
    currencyCode: line.cost?.amountPerQuantity?.currencyCode ?? 'USD',
    fromCart: true,
    cartLineId: line.id
  };
}
function getCartPicks(config: ApparelBundleSettings, cart: Cart | null): ApparelBundlePick[] {
  if (!cart) return [];
  const picks: ApparelBundlePick[] = [];
  for (const line of flattenConnection(cart.lines)) {
    if (!isApparelBundleCartLine(config, line)) continue;
    const pick = toCartPick(line);
    if (!pick) continue;

    // One entry per unit: three of one tee is three items on the ladder.
    for (let unit = 0; unit < Math.max(1, line.quantity); unit++) {
      picks.push(pick);
    }
  }
  return picks;
}

/**
 * Bundle strip state. The slots are the eligible lines already in the cart;
 * this PDP's product only prices the ladder columns until it's added. Nothing
 * here is picked by hand — the cart is the bundle.
 */
export function useApparelBundleState(offer, currentPick, fallbackCurrencyCode) {
  const $ = _c(20);
  const config = offer?.config ?? DEFAULT_APPAREL_BUNDLE_SETTINGS;
  const {
    cart,
    loadCart
  } = useCart();
  const requestedCart = useRef(false);
  let t0;
  let t1;
  if ($[0] !== cart || $[1] !== loadCart || $[2] !== offer) {
    t0 = () => {
      if (!offer || cart || requestedCart.current) {
        return;
      }
      requestedCart.current = true;
      loadCart();
    };
    t1 = [offer, cart, loadCart];
    $[0] = cart;
    $[1] = loadCart;
    $[2] = offer;
    $[3] = t0;
    $[4] = t1;
  } else {
    t0 = $[3];
    t1 = $[4];
  }
  useEffect(t0, t1);
  let t2;
  if ($[5] !== cart || $[6] !== config) {
    t2 = getCartPicks(config, cart);
    $[5] = cart;
    $[6] = config;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const cartPicks = t2;
  let next;
  if ($[8] !== cartPicks || $[9] !== config) {
    next = createEmptySlots(config);
    for (const pick of cartPicks) {
      next = fillNextSlot(next, pick);
    }
    $[8] = cartPicks;
    $[9] = config;
    $[10] = next;
  } else {
    next = $[10];
  }
  const slots = next;
  let t3;
  if ($[11] !== config || $[12] !== fallbackCurrencyCode || $[13] !== slots) {
    t3 = computeApparelBundlePricing(config, slots, fallbackCurrencyCode);
    $[11] = config;
    $[12] = fallbackCurrencyCode;
    $[13] = slots;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  const pricing = t3;
  let t4;
  if ($[15] !== config || $[16] !== currentPick || $[17] !== pricing || $[18] !== slots) {
    t4 = {
      config,
      current: currentPick,
      slots,
      pricing
    };
    $[15] = config;
    $[16] = currentPick;
    $[17] = pricing;
    $[18] = slots;
    $[19] = t4;
  } else {
    t4 = $[19];
  }
  return t4;
}